import { memo, useCallback, useEffect, useRef, useState } from "react";

const LINE_PX = { md: 22, lg: 26 };
const SIZES = {
  md: { minRows: 3, maxRows: 10, cls: "text-sm leading-[22px] px-2.5 py-2" },
  lg: { minRows: 4, maxRows: 14, cls: "text-base leading-[26px] px-3.5 py-3" },
};

// Zone de texte éditable (notions / commentaire) : multilignes, hauteur auto jusqu'à un maximum,
// puis défilement interne. Auto-save débounce + sur blur. Mémoïsée (ne se re-rend pas pour une autre ligne).
export const InlineEditableCell = memo(function InlineEditableCell({
  passportId,
  field,
  value,
  placeholder,
  onSave,
  testid,
  size = "md",
  label,
}) {
  const [text, setText] = useState(value || "");
  const timer = useRef(null);
  const lastSaved = useRef(value || "");
  const ref = useRef(null);
  const cfg = SIZES[size] || SIZES.md;
  const line = LINE_PX[size] || LINE_PX.md;

  const autosize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const min = cfg.minRows * line, max = cfg.maxRows * line;
    el.style.height = "auto";
    const h = Math.min(Math.max(el.scrollHeight, min), max);
    el.style.height = `${h}px`;
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  }, [cfg.minRows, cfg.maxRows, line]);

  // Synchronise si la valeur externe change (ex. navigation de semaine).
  useEffect(() => {
    setText(value || "");
    lastSaved.current = value || "";
  }, [value, passportId]);

  useEffect(() => { autosize(); }, [text, autosize]);

  const flush = (val) => {
    if (val === lastSaved.current) return;
    lastSaved.current = val;
    onSave(passportId, field, val);
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setText(val);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => flush(val), 600);
  };

  const handleBlur = () => {
    if (timer.current) clearTimeout(timer.current);
    flush(text);
  };

  // Mobile : garde le champ visible au-dessus du clavier tactile.
  const handleFocus = () => {
    if (size !== "lg") return;
    setTimeout(() => ref.current?.scrollIntoView({ block: "center", behavior: "smooth" }), 250);
  };

  return (
    <textarea
      ref={ref}
      rows={cfg.minRows}
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      placeholder={placeholder}
      aria-label={label}
      className={`block w-full resize-none rounded-lg border bg-white/60 text-slate-700 placeholder:text-slate-300 transition-colors duration-150 focus:border-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:bg-white/[0.02] dark:text-slate-200 dark:focus:bg-white/5 ${
        size === "lg" ? "border-slate-200 dark:border-white/15" : "border-slate-100 hover:border-slate-200 dark:border-white/5 dark:hover:border-white/10"
      } ${cfg.cls}`}
      data-testid={testid}
    />
  );
});
